import {
    Options, SeriesLineOptions, SeriesOptionsType, TooltipFormatterContextObject,
} from 'highcharts';
import { IFilter } from '@/interfaces/storageForecasting/IFilter.ts';
import {
    chartType,
    IOverviewChartDto,
    IOverviewChartPeriod,
    periodType,
} from '@/interfaces/storageForecasting/IRunOutput.ts';
import type {
    IAssetInputDto,
    IAssetStorecastData,
    TAssetTechnology,
} from '@/interfaces/storageForecasting/IAsset';
import type { IBatteryInputData } from '@/interfaces/storageForecasting/IBatteryInputData.ts';
import {
    applySharedFieldsFromBatteryToSolarWind,
    applySharedFieldsFromSolarWindToBattery,
    toAssetStorecastFormModel,
    toBatteryFormModel,
    getAssetCoreCountry,
} from '@/utils/storageForecasting/assetCoreConverter.ts';

import type { TStorageForecastCountries } from '@/types/TStorageForecastCountries.ts';
import { StorageForecastingAddOns } from '@/enums/storageForecasting/StorageForecastingAddOnsEnum.ts';
import type {
    IAssetCountryAddOnAccessFlags,
    IIsTrialAndReadOnly,
} from '@/interfaces/auth/IStorageForecastingAccess.ts';
import type { IEnactPagesAccess } from '@/interfaces/auth/IEnactPagesAccess.ts';
import {
    ASSET_READ_ONLY_MESSAGE,
    LEGACY_ASSET_GROUP_NAME_GB,
    LEGACY_ASSET_GROUP_NAME_GERMANY,
} from '@/constants/forecasting/assetConstants.ts';
import type { IAssetGroup } from '@/interfaces/storageForecasting/IAssetGroup';
import { useUserAccessStore } from '@/stores/userAccessStore.ts';
import { getChartOptions, getTooltipTableRow, getTooltipWrapper } from '@/helpers/charts/chartSettings.ts';
import { formatUnit } from '@/utils/benchmarks/flexIndexUtils.ts';
import {
    generateChartCategories,
    generateChartSeriesData,
    getYearlist,
} from '@/utils/storageForecasting/ChartUtils.ts';

export type IStorecastOutputFilters = Record<'percentile' | 'period' | 'yearStarting', string>;

export type StorecastColocationOnsiteValue = 'Standalone' | 'Solar' | 'Wind' | 'WindAndSolar';

export function hasStorecastOutputsViewAccess (pageAccess: IEnactPagesAccess): boolean {
    return pageAccess.outputs.hasAccess && pageAccess.comparison.hasAccess;
}

export function countUserEditableAssets (inputs: ReadonlyArray<IAssetInputDto>): number {
    return inputs.filter((input) => !input.isReadOnly).length;
}

export function hasReachedMaxAssetCount (
    inputs: ReadonlyArray<IAssetInputDto>,
    maxAssetCount: number,
): boolean {
    return countUserEditableAssets(inputs) >= maxAssetCount;
}

export function wouldExceedMaxAssetCount (
    inputs: ReadonlyArray<IAssetInputDto>,
    maxAssetCount: number,
    additionalCount: number,
): boolean {
    return countUserEditableAssets(inputs) + additionalCount > maxAssetCount;
}

export function isAddOnCountryResultsExportDisabled (countryAccess: IIsTrialAndReadOnly | undefined): boolean {
    if (!countryAccess) return true;
    return countryAccess.isTrial === true || countryAccess.isReadOnly === true;
}

function isStorecastTechnologyReadOnly (access: IIsTrialAndReadOnly | undefined): boolean {
    return access?.isReadOnly ?? true;
}

export function getStorecastCountryAddOnAccess (country: TStorageForecastCountries): IAssetCountryAddOnAccessFlags {
    const config = useUserAccessStore().storageForecastingConfig;
    return {
        bess: config?.addOnAccess[StorageForecastingAddOns.BessForecasting]?.countries[country],
        wind: config?.addOnAccess[StorageForecastingAddOns.WindForecasting]?.countries[country],
        solar: config?.addOnAccess[StorageForecastingAddOns.SolarForecasting]?.countries[country],
    };
}

function isBessReadOnly (country: TStorageForecastCountries): boolean {
    const access = getStorecastCountryAddOnAccess(country);
    return isStorecastTechnologyReadOnly(access.bess);
}

function isWindReadOnly (country: TStorageForecastCountries): boolean {
    const access = getStorecastCountryAddOnAccess(country);
    return isStorecastTechnologyReadOnly(access.wind);
}

function isSolarReadOnly (country: TStorageForecastCountries): boolean {
    const access = getStorecastCountryAddOnAccess(country);
    return isStorecastTechnologyReadOnly(access.solar);
}

export function isAssetResultsExportDisabledForInput (
    input: IAssetInputDto,
): boolean {
    const types = input.data.technologyList ?? [];
    const access = getStorecastCountryAddOnAccess(assetInputCountry(input));
    if (types.includes('Battery')) return isAddOnCountryResultsExportDisabled(access.bess);
    if (types.includes('Wind')) return isAddOnCountryResultsExportDisabled(access.wind);
    if (types.includes('Solar')) return isAddOnCountryResultsExportDisabled(access.solar);
    return true;
}

export function isCountryFullyLockedForAssetCreation (country: TStorageForecastCountries): boolean {
    return isBessReadOnly(country) && isWindReadOnly(country) && isSolarReadOnly(country);
}

export function hasAnyStorecastCountryAllowingAssetCreation (
    countries: ReadonlyArray<TStorageForecastCountries>,
): boolean {
    return countries.some((country) => !isCountryFullyLockedForAssetCreation(country));
}

export function isEveryStorecastCountryFullyLockedForAssetCreation (
    countries: ReadonlyArray<TStorageForecastCountries>,
): boolean {
    if (countries.length === 0) return false;
    return countries.every((country) => isCountryFullyLockedForAssetCreation(country));
}

export function assetHasBattery (technologyList: ReadonlyArray<TAssetTechnology>): boolean {
    return technologyList.includes('Battery');
}

export function assetInputCountry (asset: IAssetInputDto): TStorageForecastCountries {
    return getAssetCoreCountry(asset.data);
}

export function getAssetGroupCountry (
    group: IAssetGroup,
    inputs: ReadonlyArray<IAssetInputDto>,
): TStorageForecastCountries | null {
    if (group.assetIds.length === 0) return null;
    const assetIds = new Set(group.assetIds);
    const firstInput = inputs.find((input) => assetIds.has(input.id));
    return firstInput ? assetInputCountry(firstInput) : null;
}

export function isAssetGroupCompatibleWithCountry (
    group: IAssetGroup,
    inputs: ReadonlyArray<IAssetInputDto>,
    country: TStorageForecastCountries,
): boolean {
    const groupCountry = getAssetGroupCountry(group, inputs);
    return groupCountry === null || groupCountry === country;
}

const ASSET_GROUP_COUNTRY_MISMATCH_MESSAGE = 'Asset groups can only contain assets of one country';

export function getAssetGroupCountryMismatchTooltip (
    group: IAssetGroup,
    inputs: ReadonlyArray<IAssetInputDto>,
    country: TStorageForecastCountries | null | undefined,
): string | null {
    if (!country || isAssetGroupCompatibleWithCountry(group, inputs, country)) return null;
    return ASSET_GROUP_COUNTRY_MISMATCH_MESSAGE;
}

const LEGACY_ASSET_GROUP_NAMES = new Set<string>([
    LEGACY_ASSET_GROUP_NAME_GB,
    LEGACY_ASSET_GROUP_NAME_GERMANY,
]);

export function isLegacyAssetGroup (group: Pick<IAssetGroup, 'name'>): boolean {
    return LEGACY_ASSET_GROUP_NAMES.has(group.name);
}

export function formatAssetTechnologyLabel (technologyList: ReadonlyArray<TAssetTechnology>): string {
    if (technologyList.length === 0) return '—';
    return technologyList.join(', ');
}

export function filterAssetInputsByCountry (inputs: ReadonlyArray<IAssetInputDto>, country: TStorageForecastCountries | null | undefined): Array<IAssetInputDto> {
    if (!country) return [];
    return inputs.filter((input) => assetInputCountry(input) === country);
}

export function filterBatteryAssetInputs (inputs: ReadonlyArray<IAssetInputDto>, country?: TStorageForecastCountries | null): Array<IAssetInputDto> {
    return inputs.filter((input) => {
        if (!assetHasBattery(input.data.technologyList ?? [])) return false;
        if (country && assetInputCountry(input) !== country) return false;
        return true;
    });
}

export function filterNonBatteryAssetInputs (inputs: ReadonlyArray<IAssetInputDto>, country?: TStorageForecastCountries | null): Array<IAssetInputDto> {
    return inputs.filter((input) => {
        if (assetHasBattery(input.data.technologyList ?? [])) return false;
        if (country && assetInputCountry(input) !== country) return false;
        return true;
    });
}

export function isStandaloneWindOrSolarSelection (types: ReadonlyArray<TAssetTechnology>): boolean {
    const hasBattery = types.includes('Battery');
    const hasWindOrSolar = types.some((t) => t === 'Solar' || t === 'Wind');
    return hasWindOrSolar && !hasBattery;
}

export function getBessBatteryAccessBlockMessage (
    country: TStorageForecastCountries,
    types: ReadonlyArray<TAssetTechnology>,
): string | null {
    if (!assetHasBattery(types)) return null;
    if (!isBessReadOnly(country)) return null;
    return ASSET_READ_ONLY_MESSAGE;
}

export function getWindSolarStandaloneAccessBlockMessage (
    country: TStorageForecastCountries,
    types: ReadonlyArray<TAssetTechnology>,
): string | null {
    if (!isStandaloneWindOrSolarSelection(types)) return null;
    const hasWind = types.includes('Wind');
    const hasSolar = types.includes('Solar');

    if (hasWind && isWindReadOnly(country) && hasSolar && isSolarReadOnly(country)) {
        return 'You have read-only access for wind and solar forecasting in this country. Standalone wind and solar assets cannot be modified.';
    }
    if (hasWind && isWindReadOnly(country)) {
        return 'You have read-only access for wind forecasting in this country. Standalone wind assets cannot be modified; co-locate with a battery to configure wind.';
    }
    if (hasSolar && isSolarReadOnly(country)) {
        return 'You have read-only access for solar forecasting in this country. Standalone solar assets cannot be modified; co-locate with a battery to configure solar.';
    }
    return null;
}

export function getAssetMutationAccessBlockMessageForInput (
    input: IAssetInputDto,
): string | null {
    const types = input.data.technologyList ?? [];
    const country = assetInputCountry(input);
    const bessBatteryMessage = getBessBatteryAccessBlockMessage(country, types);
    if (bessBatteryMessage) return bessBatteryMessage;
    return getWindSolarStandaloneAccessBlockMessage(country, types);
}

export function isAssetTypeChoiceDisabledForBessAccess (
    type: TAssetTechnology,
    country: TStorageForecastCountries,
): boolean {
    return type === 'Battery' && isBessReadOnly(country);
}

export function isAssetTypeChoiceDisabledForWindSolarAccess (
    type: TAssetTechnology,
    selectedTypes: ReadonlyArray<TAssetTechnology>,
    country: TStorageForecastCountries,
): boolean {
    const hasBattery = selectedTypes.includes('Battery');
    if (type === 'Wind' && isWindReadOnly(country) && !hasBattery) return true;
    if (type === 'Solar' && isSolarReadOnly(country) && !hasBattery) return true;
    return false;
}

export function getAssetTypeChoiceDisabledTooltip (
    type: TAssetTechnology,
    country: TStorageForecastCountries,
): string | null {
    if (type === 'Battery' && isBessReadOnly(country)) {
        return 'Read-only BESS forecasting access. Battery assets cannot be modified in this country.';
    }
    if (type === 'Wind' && isWindReadOnly(country)) {
        return 'Read-only wind forecasting access. Select battery to configure co-located wind.';
    }
    if (type === 'Solar' && isSolarReadOnly(country)) {
        return 'Read-only solar forecasting access. Select battery to configure co-located solar.';
    }
    return null;
}

export interface IMarginOverlaySeriesInput {
    assetLabel: string;
    categories: string[];
    totals: number[];
    yAxisUnit: string;
}

function alignTotalsToCanonicalCategories (canonicalCategories: string[], categories: string[], totals: number[]): Array<number | null> {
    if (
        canonicalCategories.length === categories.length
        && canonicalCategories.every((c, i) => c === categories[i])
    ) {
        return totals.map((t) => (Number.isFinite(t) ? t : null));
    }
    const valueByCategory = new Map<string, number>();
    categories.forEach((cat, i) => {
        const v = totals[i];
        if (typeof v === 'number' && Number.isFinite(v)) {
            valueByCategory.set(cat, v);
        }
    });
    return canonicalCategories.map((cat) => {
        const v = valueByCategory.get(cat);
        return v !== undefined ? v : null;
    });
}

export function marginTotalsFromOverviewEntry (
    entry: IOverviewChartDto[number] | null | undefined,
    filters: IStorecastOutputFilters | null,
): { categories: string[]; totals: number[]; yAxisUnit: string } | null {
    if (!entry || !filters) {
        return null;
    }
    const percentile = filters.percentile as chartType;
    const period = filters.period as periodType;
    const chartPeriod: IOverviewChartPeriod | undefined = entry[percentile]?.[period];
    if (!chartPeriod) {
        return null;
    }
    const pointStart = new Date(chartPeriod.pointStart);
    const pointEnd = new Date(chartPeriod.pointEnd);
    const yearOptions = getYearlist(pointStart, pointEnd);
    if (yearOptions.length === 0) {
        return null;
    }
    const yearStarting = yearOptions.includes(filters.yearStarting)
        ? filters.yearStarting
        : yearOptions[0];
    const chartFilter: IFilter = {
        period: { id: filters.period, name: filters.period },
        yearStarting: { id: yearStarting, name: yearStarting },
    };
    const chartSeriesShape = {
        ...chartPeriod,
        pointStart,
        pointEnd,
        metricByYear: {},
    };
    const seriesRows = generateChartSeriesData(chartSeriesShape, chartFilter, yearOptions, pointStart);
    const categories = generateChartCategories(chartFilter, pointStart, pointEnd);
    if (seriesRows.length === 0 || categories.length === 0) {
        return null;
    }
    const totals = categories.map((_, i) => seriesRows.reduce((sum, row) => {
        const v = row.data[i];
        return sum + (typeof v === 'number' && Number.isFinite(v) ? v : 0);
    }, 0));
    return {
        categories,
        totals,
        yAxisUnit: chartPeriod.unit,
    };
}

function storecastMarginOverlayTooltipFormatter (this: TooltipFormatterContextObject): string {
    let rows = '';
    const headerLabel = this.key !== undefined && this.key !== null ? String(this.key) : String(this.x ?? '');
    this.points?.forEach((point: TooltipFormatterContextObject) => {
        const rawY = point.y;
        if (rawY === null || rawY === undefined || !Number.isFinite(Number(rawY))) {
            return;
        }
        const y = Number(rawY)
            .toFixed(2)
            .toString()
            .replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        const suffix = formatUnit(
            (point.series.userOptions as SeriesOptionsType & { unit?: string }).unit ?? null,
        );
        rows += getTooltipTableRow({
            label: point.series.name,
            value: `${y}${suffix ? ` (${suffix})` : ''}`,
            color: point.color,
        });
    });
    return getTooltipWrapper({
        content: rows,
        contentType: 'table',
        header: headerLabel,
    });
}

function pickCanonicalMarginCategories (inputs: Array<IMarginOverlaySeriesInput>): string[] {
    if (inputs.length === 0) {
        return [];
    }
    const longest = [...inputs].sort((a, b) => b.categories.length - a.categories.length)[0];
    return longest.categories;
}

export function buildStorecastMarginOverlayChartOptions (seriesInputs: Array<IMarginOverlaySeriesInput>, darkMode: boolean, chartColours: string[]): Options {
    const canonicalCategories = seriesInputs.length === 0
        ? []
        : pickCanonicalMarginCategories(seriesInputs);
    const yAxisTitle = seriesInputs[0]?.yAxisUnit ?? 'Margin';
    const base = getChartOptions(darkMode, false);

    const series: Array<SeriesLineOptions> = seriesInputs.map((input, assetIndex) => {
        const alignedData = alignTotalsToCanonicalCategories(
            canonicalCategories,
            input.categories,
            input.totals,
        );
        return {
            type: 'line',
            name: input.assetLabel,
            data: alignedData,
            color: chartColours[assetIndex % chartColours.length],
            unit: input.yAxisUnit,
        };
    });

    return {
        ...base,
        chart: {
            ...base.chart,
            type: 'line',
            animation: false,
            backgroundColor: 'transparent',
        },
        legend: {
            ...base.legend,
            enabled: true,
        },
        xAxis: {
            categories: canonicalCategories,
        },
        yAxis: {
            ...base.yAxis,
            title: {
                ...(typeof base.yAxis === 'object' && !Array.isArray(base.yAxis) ? base.yAxis.title : {}),
                text: yAxisTitle,
            },
            min: 0,
        },
        plotOptions: {
            ...base.plotOptions,
            line: {
                marker: {
                    enabled: true,
                },
            },
        },
        tooltip: {
            ...base.tooltip,
            shared: true,
            formatter: storecastMarginOverlayTooltipFormatter,
        },
        series,
    };
}

export function mapAssetTypesToStorecastOnsiteOption (types: ReadonlyArray<TAssetTechnology>): StorecastColocationOnsiteValue {
    const hasSolar = types.includes('Solar');
    const hasWind = types.includes('Wind');
    if (hasSolar && hasWind) return 'WindAndSolar';
    if (hasSolar) return 'Solar';
    if (hasWind) return 'Wind';
    return 'Standalone';
}

export function mapStorecastOnsiteOptionToAssetTypes (option: string | null | undefined): Array<TAssetTechnology> {
    if (option === 'WindAndSolar') return ['Battery', 'Solar', 'Wind'];
    if (option === 'Solar') return ['Battery', 'Solar'];
    if (option === 'Wind') return ['Battery', 'Wind'];
    return ['Battery'];
}

export interface IAddAssetFormPrefillState {
    selectedCountry: TStorageForecastCountries;
    selectedTypes: Array<TAssetTechnology>;
    batteryAssetData: IBatteryInputData | null;
    solarWindAssetData: IAssetStorecastData | null;
}

/** Maps an existing input DTO into add-asset form state (clears ids so save type New creates a new record). */
export function buildAddAssetFormStateFromInput (asset: IAssetInputDto): IAddAssetFormPrefillState {
    const selectedCountry = asset.data.country;

    if (assetHasBattery(asset.data.technologyList ?? [])) {
        const selectedTypes = mapStorecastOnsiteOptionToAssetTypes(asset.data.storecastOnsiteOption);
        const batteryAssetData: IBatteryInputData = {
            ...toBatteryFormModel(asset),
            id: null,
        };
        const solarWindAssetData = applySharedFieldsFromBatteryToSolarWind(
            batteryAssetData,
            null,
            selectedCountry,
            selectedTypes,
        );
        return {
            selectedCountry,
            selectedTypes,
            batteryAssetData,
            solarWindAssetData,
        };
    }

    const selectedTypes = (asset.data.technologyList ?? []).filter(
        (t): t is TAssetTechnology => t === 'Solar' || t === 'Wind',
    );
    const solarWindAssetData = toAssetStorecastFormModel(asset, selectedTypes);
    const batteryAssetData = applySharedFieldsFromSolarWindToBattery(
        solarWindAssetData,
        null,
        selectedCountry,
        selectedTypes,
    );
    return {
        selectedCountry,
        selectedTypes,
        batteryAssetData,
        solarWindAssetData,
    };
}
